import { AfterViewInit, ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, input, model, OnDestroy, output, signal, Signal } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { PasswordComponent, PasswordComponentValue } from 'src/app/controls/components/basic/password/password.component';
import { User } from 'src/app/user/models';
import { CurrentUserService } from 'src/app/user/services';

@Component({
  selector: 'app-password-field',
  templateUrl: './password-field.component.html',
  styleUrl: './password-field.component.scss',
  imports: [FormsModule, ReactiveFormsModule, PasswordComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PasswordFieldComponent implements AfterViewInit, OnDestroy {
  // DI
  private currentUserService = inject(CurrentUserService);
  private elementRef = inject(ElementRef<HTMLElement>) as ElementRef<HTMLElement>;

  // Public input signals
  disabled = input(false);
  secretId = input<string | null>(null);
  formValidationGroup = input<FormGroup | null>(null);
  validationIdentifier = input<string>(null);
  value = model<string>('');

  // Public output signals
  cleared = output<void>();

  // Public (non-input) signals
  allowShowPassword = computed(() => this.currentUser() && User.hasPermission('ShowPasswords', this.currentUser()));
  passwordComponentValue = computed<PasswordComponentValue | null>(() => this.secretId() || this.value() ? { id: this.secretId() ?? null, value: this.value() ?? null } : null);
  effectiveDisabled = computed(() => this.disabled() || this.fieldsetDisabled());

  // Private signals
  private currentUser: Signal<User | null> = toSignal(this.currentUserService.userChangedObserver, { initialValue: this.currentUserService.getCurrentUser() });
  private formControl = computed(() => this.formValidationGroup() && this.validationIdentifier() ? this.formValidationGroup().get(this.validationIdentifier()) as UntypedFormControl : null);
  private fieldsetDisabled = signal(false);

  // Private fields
  private fieldsetElement: HTMLFieldSetElement | null = null;
  private fieldsetObserver: MutationObserver | null = null;

  constructor() {
    effect(() => {
      const ctrl = this.formControl();
      if (ctrl) {
        const disabled = this.disabled();
        if (disabled !== ctrl.disabled) {
          if (disabled) ctrl.disable({ emitEvent: false });
          else ctrl.enable({ emitEvent: false });
        }
      }
    });
  }

  ngAfterViewInit(): void {
    // Find nearest ancestor fieldset once (DOM structure assumed stable)
    this.fieldsetElement = this.elementRef.nativeElement.closest('fieldset');

    if (this.fieldsetElement) {
      // Initial state
      this.fieldsetDisabled.set(this.fieldsetElement.disabled);

      // Observe only the disabled attribute
      this.fieldsetObserver = new MutationObserver(muts => {
        if (muts.some(m => m.attributeName === 'disabled')) {
          this.fieldsetDisabled.set(this.fieldsetElement.disabled);
        }
      });
      this.fieldsetObserver.observe(this.fieldsetElement, { attributes: true, attributeFilter: ['disabled'] });
    }
  }

  ngOnDestroy(): void {
    this.fieldsetObserver?.disconnect();
  }

  // Public methods
  valueChange(value: PasswordComponentValue | null) {
    this.value.set(value?.value ?? '');
    if (this.formControl()) {
      this.formControl().patchValue(value?.value ?? '', { emitEvent: false });
    }
    if (value === null) {
      this.cleared.emit();
    }
    this.formControl()?.updateValueAndValidity();
  }
}
